const hiddenClass = "selling_plan_theme_integration--hidden";
let globalFrequencyPreference = null; // 'monthly' or 'annual'

// Get translations from global object or fallback to defaults
const getTranslation = (key, fallback) => {
  return window.sellingPlanTranslations?.[key] || fallback;
};

const translations = {
  monthlyPayment: getTranslation('monthlyPayment', 'Monthly payment'),
  annualPayment: getTranslation('annualPayment', 'Annual payment'),
  perMonth: getTranslation('perMonth', '/per month'),
  perYear: getTranslation('perYear', '/per year')
};

function getCurrentLanguage() {
  // Check multiple Shopify locale sources
  if (window.Shopify?.locale) {
    return window.Shopify.locale;
  }
  
  // Check if locale is in a meta tag (common Shopify pattern)
  const localeMeta = document.querySelector('meta[name="shopify-locale"]');
  if (localeMeta) {
    return localeMeta.content;
  }
  
  // Check for locale in script tag data
  const localeScript = document.querySelector('script[data-locale]');
  if (localeScript) {
    return localeScript.dataset.locale;
  }
  
  return 'nl'; // fallback
}

// Define country mappings
function getCountriesForLanguage(language) {
  const countryMappings = {
    'nl': ['Nederland', 'Belgie'],
    'fr': ['Belgique', 'France', 'Suisse'],
    'de': ['Deutschland', 'Schweiz', 'Österreich'],
    'es': ['Francia'], 
    'en': ['Netherlands', 'Belgium', 'Germany', 'France', 'Austria', 'Switzerland']
  };
  
  return countryMappings[language] || countryMappings['en'];
}

function getHost(el) {
  return el?.closest?.('.frequency-option') || el;
}
function getLabelText(el) {
  const host = getHost(el);
  return host?.querySelector('.bls_swatche-item')?.textContent?.trim() || '';
}
function getRadioType(el) {
  const host = getHost(el);
  return (host?.dataset.radioType || el?.dataset?.radioType || '').toLowerCase();
}
function isSubscription(el) {
  return getRadioType(el) === 'selling_plan';
}
function inferUnitFromLabel(label) {
  // very permissive, supports multiple languages
  const l = label.toLowerCase();
  const monthWords = /(month|mois|monat|mese|mes|maand|mensual|mensuel)/i;  // en/fr/de/it/es/nl
  const yearWords  = /(year|année|jahr|anno|año|jaar|annual|annually|anual|annuel|annuel|jährliche)/i;    // en/fr/de/it/es/nl
  if (monthWords.test(l)) return 'month';
  if (yearWords.test(l))  return 'year';
  return ''; // unknown
}
function getCadence(el) {
  const host = getHost(el);
  const unitAttr  = (host?.dataset.interval || el?.dataset?.interval || '').toLowerCase();
  const countAttr = parseInt(host?.dataset.intervalCount || el?.dataset?.intervalCount || '1', 10);
  if (unitAttr) return { unit: unitAttr, count: countAttr };

  // fallback: try to infer from visible label (keeps NL working even w/o data-attrs)
  const label = getLabelText(el);
  return { unit: inferUnitFromLabel(label), count: 1 };
}

class SellingPlansWidget {
  constructor(sellingPlansWidgetContainer) {
    this.container = sellingPlansWidgetContainer;
    this.sectionId = this.container.getAttribute("data-section-id");
    this.section = document.querySelector(`#shopify-section-${this.sectionId}`);

    this.init();
  }

  init() {
    this.appendSellingPlanInputs();
    this.reorderFrequencyOptions();
    this.filterCountriesByLanguage();
    this.handleVariantChange();
    this.setInitialPreference();
    this.setupEventListeners();
    this.enforceBTWPreference();
  }

  get currentVariantId() {
    const variantInput = this.section.querySelector(
      'input[name="id"], select[name="id"]'
    );
    return variantInput?.value;
  }

  get currentSellingPlanForm() {
    return this.section.querySelector(
      `section[data-variant-id="${this.currentVariantId}"]`
    );
  }

  get mainPriceElement() {
    return this.section.querySelector(".price__regular .price");
  }

  appendSellingPlanInputs() {
    const sellingPlanInput = this.section.querySelector(
      ".selected-selling-plan-id"
    );
    const forms = this.section.querySelectorAll('[action*="/cart/add"]');

    forms.forEach((form) => {
      if (!form.querySelector(".selected-selling-plan-id")) {
        form.appendChild(sellingPlanInput.cloneNode());
      }
    });
  }

  reorderFrequencyOptions() {
    const allForms = this.section.querySelectorAll('.selling_plan_theme_integration');
    
    allForms.forEach((form) => {
      const plansList = form.querySelector('.selling_plan_app_plans_list');
      if (!plansList) return;

      const items = Array.from(plansList.querySelectorAll('.frequency-option-item'));
      
      // Sort items: yearly first, then monthly
      const sortedItems = items.sort((a, b) => {
        const aText = a.querySelector('.bls_swatche-item')?.textContent || '';
        const bText = b.querySelector('.bls_swatche-item')?.textContent || '';
        
        // Use translations for comparison
        const aIsYearly = aText.includes(translations.annualPayment);
        const bIsYearly = bText.includes(translations.annualPayment);
        const aIsMonthly = aText.includes(translations.monthlyPayment);
        const bIsMonthly = bText.includes(translations.monthlyPayment);
        
        // One-time purchase should stay first if it exists
        if (aText.includes('One-time')) return -1;
        if (bText.includes('One-time')) return 1;
        
        // Yearly before monthly
        if (aIsYearly && bIsMonthly) return -1;
        if (aIsMonthly && bIsYearly) return 1;
        
        return 0;
      });

      // Clear and re-append in correct order
      plansList.innerHTML = '';
      sortedItems.forEach(item => plansList.appendChild(item));
    });
  }

  filterCountriesByLanguage() {
  const currentLanguage = getCurrentLanguage();
  const allowedCountries = getCountriesForLanguage(currentLanguage);

  // Filter the country dropdown
  const countryDropdown = this.section.querySelector('.field-option-1 .bls__color-dropdown-content');
  if (countryDropdown) {
    const countryOptions = countryDropdown.querySelectorAll('.bls__product-color-swatches-dropdown');
    
    let firstAvailableCountry = null;
    
    countryOptions.forEach(option => {
      const countryName = option.querySelector('label').textContent.trim();
      if (allowedCountries.includes(countryName)) {
        option.style.display = 'block';
        // Set the first available country as default
        if (!firstAvailableCountry) {
          firstAvailableCountry = countryName;
          option.classList.add('active');
        } else {
          option.classList.remove('active');
        }
      } else {
        option.style.display = 'none';
        option.classList.remove('active');
      }
    });
    
    // Update the dropdown display text
    const dropdownValue = this.section.querySelector('.field-option-1 .bls__color-dropdown-value');
    if (dropdownValue && firstAvailableCountry) {
      dropdownValue.textContent = firstAvailableCountry;
    }
    
    // Update the selected value display
    const selectedValueSpan = this.section.querySelector('.field-option-1 .swatch-selected-value');
    if (selectedValueSpan && firstAvailableCountry) {
      selectedValueSpan.textContent = firstAvailableCountry;
    }
    
    // Update the select dropdown and trigger change
    this.filterSelectOptions(allowedCountries, firstAvailableCountry);

    const variantSelect = this.section.querySelector('select[name="id"]');
    if (variantSelect) {
      const firstEnabled = Array.from(variantSelect.options).find(opt => !opt.disabled);
      if (firstEnabled) {
        variantSelect.value = firstEnabled.value;
        variantSelect.dispatchEvent(new Event("change", { bubbles: true }));
        
        // Also update the URL so Shopify doesn’t force the old variant back
        const newUrl = new URL(window.location.href);
        newUrl.searchParams.set('variant', firstEnabled.value);
        window.history.replaceState({}, '', newUrl);
      }
    }

      if (firstAvailableCountry) {
        // 1. Find the matching dropdown option (the visible swatch)
        const firstOptionEl = Array.from(countryOptions).find(option => {
          const countryName = option.querySelector('label').textContent.trim();
          return countryName === firstAvailableCountry;
        });

        if (firstOptionEl) {
          const radioInput = firstOptionEl.querySelector('input[type="radio"]');
          if (radioInput) {
            // Select it for real
            radioInput.checked = true;
            radioInput.dispatchEvent(new Event("change", { bubbles: true }));
          }

          // Optional: simulate a click to trigger all cosmetic updates
          firstOptionEl.dispatchEvent(new Event("click", { bubbles: true }));
        }
      }
    }
  }

  filterSelectOptions(allowedCountries, defaultCountry = null) {
    const variantSelect = this.section.querySelector('select[name="id"]');
    if (!variantSelect) return;

    const options = Array.from(variantSelect.options);
    let firstAvailableOption = null;
    
    options.forEach(option => {
      // Extract country from option text (assumes format: "Country / Option2 / Option3")
      const optionParts = option.text.split(' / ');
      if (optionParts.length > 0) {
        const country = optionParts[0].trim();
        if (!allowedCountries.includes(country)) {
          option.style.display = 'none';
          option.disabled = true;
        } else {
          option.style.display = 'block';
          option.disabled = false;
          
          // Set first available option as default if it matches the default country
          if (!firstAvailableOption || (defaultCountry && country === defaultCountry)) {
            firstAvailableOption = option;
          }
        }
      }
    });
    
    // Select the first available option and trigger change
    if (firstAvailableOption && variantSelect.value !== firstAvailableOption.value) {
      variantSelect.value = firstAvailableOption.value;
      variantSelect.dispatchEvent(new Event("change", { bubbles: true }));
    }
  }

  handleVariantChange() {
    this.updateFormVisibility();
    this.reorderFrequencyOptions();
    this.enforceBTWPreference();
    this.setupRadioListeners();
    
    // Apply frequency preference after variant change
    setTimeout(() => {
      this.applyFrequencyPreference();
    }, 100);
  }

  setInitialPreference() {
    if (globalFrequencyPreference) return;

    const checked = this.currentSellingPlanForm?.querySelector('input[type="radio"]:checked');
    if (!checked) return;

    const { unit } = getCadence(checked);
    if (unit === 'month') globalFrequencyPreference = 'monthly';
    else if (unit === 'year') globalFrequencyPreference = 'annual';
  }

  updateFormVisibility() {
    const allForms = this.section.querySelectorAll(
      ".selling_plan_theme_integration"
    );
    const currentForm = this.currentSellingPlanForm;

    allForms.forEach((form) => {
      if (form === currentForm) {
        form.classList.remove(hiddenClass);
      } else {
        form.classList.add(hiddenClass);
      }
    });
  }

  applyFrequencyPreference() {
   if (!globalFrequencyPreference || !this.currentSellingPlanForm) return;
    const radios = this.currentSellingPlanForm.querySelectorAll('input[type="radio"]');
    // uncheck all first
    radios.forEach(r => r.checked = false);

    let found = false;
    radios.forEach((r) => {
      const { unit } = getCadence(r);
      if (
        (globalFrequencyPreference === 'monthly' && unit === 'month') ||
        (globalFrequencyPreference === 'annual'  && unit === 'year')
      ) {
        r.checked = true;
        found = true;
      }
    });

    if (!found && radios[0]) radios[0].checked = true;

    this.updatePrice();
    this.updateHiddenInputs();
    this.updateSwatchVisualState();
  }

  updateSwatchVisualState() {
    if (!this.currentSellingPlanForm) return;

    const checkedRadio = this.currentSellingPlanForm.querySelector('input[type="radio"]:checked');
    const swatches = this.currentSellingPlanForm.querySelectorAll('.frequency-option');
    
    // Remove active from all swatches
    swatches.forEach(swatch => {
      swatch.classList.remove('active');
    });

    // Add active to the correct swatch
    if (checkedRadio) {
      const activeSwatch = checkedRadio.closest('.frequency-option');
      if (activeSwatch) {
        activeSwatch.classList.add('active');
        
        // Update the selected value display
        const fieldset = this.currentSellingPlanForm.querySelector('fieldset');
        const selectedValueSpan = fieldset?.querySelector('.frequency-selected-value');
        const labelText = activeSwatch.querySelector('.bls_swatche-item')?.textContent;
        
        if (selectedValueSpan && labelText) {
          selectedValueSpan.textContent = labelText.trim();
        }
      }
    }
  }

  updatePrice() {
    const checked = this.currentSellingPlanForm?.querySelector('input[type="radio"]:checked');
    if (!checked || !this.mainPriceElement) return;

    const price = checked.dataset.variantPrice;
    if (!price) return;

    const numericPrice = price
      .replace(/[€$£¥]/g, "")
      .replace(/\s*(EUR|USD|GBP|JPY)\s*/gi, "")
      .trim();

    // NEW: robust cadence + only show suffix for subscriptions
    let suffix = "";
    if (isSubscription(checked)) {
      const { unit, count } = getCadence(checked);
      if (unit === 'year')  suffix = translations.perYear;
      if (unit === 'month') suffix = translations.perMonth;
      if (count > 1 && (unit === 'month' || unit === 'year')) {
        suffix = suffix.replace('/per ', `/per ${count} `);
      }
    }

    this.mainPriceElement.innerHTML =
      `${numericPrice}${suffix ? `<span class="frequency-text">${suffix}</span>` : ""}`;
  }

  updateHiddenInputs() {
    const checkedRadio = this.currentSellingPlanForm?.querySelector(
      'input[type="radio"]:checked'
    );
    const sellingPlanId = checkedRadio?.dataset.sellingPlanId || "";

    const hiddenInputs = this.section.querySelectorAll(
      ".selected-selling-plan-id"
    );
    hiddenInputs.forEach((input) => {
      input.value = sellingPlanId;
    });
  }

  setupRadioListeners() {
    if (!this.currentSellingPlanForm) return;

    const radios = this.currentSellingPlanForm.querySelectorAll('input[type="radio"]');

    radios.forEach((radio) => {
      // Remove existing handler if it exists
      if (radio._radioChangeHandler) {
        radio.removeEventListener("change", radio._radioChangeHandler);
      }
      
      // Create new handler and store reference
      radio._radioChangeHandler = this.handleRadioChange.bind(this);
      radio.addEventListener("change", radio._radioChangeHandler);
    });
  }

  handleRadioChange(event) {
    const radio = event.target;
    const { unit } = getCadence(radio);
    if (unit === 'month') globalFrequencyPreference = 'monthly';
    else if (unit === 'year') globalFrequencyPreference = 'annual';

    this.updatePrice();
    this.updateHiddenInputs();
    this.updateSwatchVisualState();
  }

  setupEventListeners() {
    // Remove existing listeners first to prevent duplicates
    const forms = this.section.querySelectorAll('[action*="/cart/add"]');
    forms.forEach((form) => {
      // Remove existing handler if it exists
      if (form._variantChangeHandler) {
        form.removeEventListener("change", form._variantChangeHandler);
      }
      
      // Create new handler and store reference
      form._variantChangeHandler = (event) => {
        if (event.target.name === "id") {
          setTimeout(() => {
            this.handleVariantChange();
          }, 200);
        }
      };
      
      form.addEventListener("change", form._variantChangeHandler);
    });

    const variantSelects = this.section.querySelector("variant-selects");
    if (variantSelects) {
      const swatches = variantSelects.querySelectorAll(".bls__option-swatch");
      swatches.forEach((swatch) => {
        // Remove existing handler if it exists
        if (swatch._variantClickHandler) {
          swatch.removeEventListener("click", swatch._variantClickHandler);
        }
        
        // Create new handler and store reference
        swatch._variantClickHandler = () => {
          setTimeout(() => {
            this.handleVariantChange();
          }, 200);
        };
        
        swatch.addEventListener("click", swatch._variantClickHandler);
      });
    }
  }

  enforceBTWPreference() {
    const variantSelect = this.section.querySelector('select[name="id"]');
    if (!variantSelect) return;

    const inclOption = Array.from(variantSelect.options).find(
      (opt) => opt.dataset.option3?.trim() === "incl. BTW"
    );

    if (inclOption) {
      variantSelect.value = inclOption.value;
      variantSelect.dispatchEvent(new Event("change", { bubbles: true }));

      const btwFieldset = this.section.querySelector(".field-option-btw");
      const selectedText = btwFieldset?.querySelector(".swatch-selected-value");
      if (selectedText) selectedText.textContent = "incl. BTW";
    }
  }
}

// Initialize function with protection against multiple initializations
function initSellingPlans() {
  // Prevent multiple initializations
  if (window.sellingPlansInitialized) return;
  window.sellingPlansInitialized = true;
  
  document.querySelectorAll(".selling_plan_app_container").forEach((container) => {
    if (!container._widget) {
      container._widget = new SellingPlansWidget(container);
    }
  });
}

// Simple swatch handler with protection against multiple initializations
function initFrequencySwatches() {
  // Skip if already initialized
  if (window.frequencySwatchesInitialized) return;
  window.frequencySwatchesInitialized = true;
  
  const frequencyOptions = document.querySelectorAll('.frequency-option');
  
  frequencyOptions.forEach(option => {
    // Skip if already has handler
    if (option._hasClickHandler) return;
    option._hasClickHandler = true;
    
    const handleClick = function(e) {
      e.preventDefault();
      
      if (this.classList.contains('disabled')) return;
      
      const fieldset = this.closest('fieldset');
      const radioInput = this.querySelector('input[type="radio"]');
      const selectedValueSpan = fieldset.querySelector('.frequency-selected-value');
      
      // Visual updates
      fieldset.querySelectorAll('.frequency-option').forEach(opt => {
        opt.classList.remove('active');
      });
      this.classList.add('active');
      
      // Update display text
      if (selectedValueSpan) {
        const labelText = this.querySelector('.bls_swatche-item')?.textContent;
        if (labelText) {
          selectedValueSpan.textContent = labelText;
        }
      }
      
      // Trigger radio change
      if (radioInput && !radioInput.checked) {
        radioInput.checked = true;
        radioInput.dispatchEvent(new Event('change', { bubbles: true }));
      }
    };
    
    option.addEventListener('click', handleClick);
  });
}

function handleLanguageChange() {
  // Re-initialize widgets when language changes
  document.querySelectorAll(".selling_plan_app_container").forEach((container) => {
    if (container._widget) {
      container._widget.filterCountriesByLanguage();
    }
  });
}

// Initialize everything with proper timing
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', function() {
    setTimeout(() => {
      initSellingPlans();
      initFrequencySwatches();
    }, 500);
  });
} else {
  setTimeout(() => {
    initSellingPlans();
    initFrequencySwatches();
  }, 500);
}

// Listen for language changes (if your site supports dynamic language switching)
document.addEventListener('shopify:section:load', handleLanguageChange);
window.addEventListener('popstate', handleLanguageChange);